import React, { useEffect, useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Theme from '@amzn/meridian/theme';
import Heading from '@amzn/meridian/heading';
import { colorBlue600 } from '@amzn/meridian-tokens/base/color';
import { isShiftInProgress } from '../utils';
import EosKpiCards from './EosKpiCards';
import EosPathTable from './EosPathTable';
import EosWash from './EosWash';
import { EosReportingData, EosShiftData, fetchEosReportingData } from './eosReportingData';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectStationCode } from '../../../../store/slices/shiftPlanSlice';
import { showError } from '../../../../store/slices/errorSlice';
import { ShiftTypes } from '../../../../types/shiftPlan';
import ReportPlanSelector from './ReportPlanSelector';
import { useReportPlans } from './useReportPlans';
import { resolveDefaultPlanId } from './reportPlanOptions';

/**
 * Consolidated EOS Reporting view. A Day / Night / Combined toggle selects which
 * shift block(s) render; each block shows a section label, 4 KPI cards, a Process
 * Path Performance table (Primary/Support toggle), and an EOS Wash panel.
 */

type ShiftView = 'day' | 'night' | 'combined';

const VIEW_OPTIONS: { value: ShiftView; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
  { value: 'combined', label: 'Combined' },
];

// Skew-correction + live re-tick, mirroring ShiftTimeline: derive "now" from the viewer's
// clock corrected by the response's serverTime, ignoring sub-30s skew.
const HALF_MINUTE_MS = 30 * 1000;
const REFRESH_MS = 30 * 1000;

const ShiftBlock: React.FC<{ shift: EosShiftData }> = ({ shift }) => {
  const skewMs = useMemo(() => {
    const serverTime = shift.shiftTimeContext?.serverTime;
    if (!serverTime) return 0;
    const serverMs = new Date(serverTime).getTime();
    if (Number.isNaN(serverMs)) return 0;
    const skew = serverMs - Date.now();
    return Math.abs(skew) < HALF_MINUTE_MS ? 0 : skew;
  }, [shift.shiftTimeContext?.serverTime]);

  const [nowMs, setNowMs] = useState(() => Date.now() + skewMs);

  // Re-tick every 30s so the pill clears the moment the shift ends while viewing.
  useEffect(() => {
    setNowMs(Date.now() + skewMs);
    const id = setInterval(() => setNowMs(Date.now() + skewMs), REFRESH_MS);
    return () => clearInterval(id);
  }, [skewMs]);

  const inProgress = isShiftInProgress(shift.shiftTimeContext, nowMs);

  return (
    <Column spacing="200" data-testid={`eos-shift-block-${shift.key}`}>
      <Row spacing="200" widths="fit" alignmentVertical="center">
        <Text type="h100" color="secondary">
          {shift.label.toUpperCase()} ({shift.timeRange})
        </Text>
        {inProgress && (
          <Tag type="warning" data-testid={`eos-in-progress-${shift.key}`}>
            In progress
          </Tag>
        )}
      </Row>
      <EosKpiCards shift={shift} />
      <EosPathTable shift={shift} />
      <EosWash
        title={`EOS Wash — ${shift.label}`}
        text={shift.wash}
        testId={`eos-wash-${shift.key}`}
      />
    </Column>
  );
};

interface EndOfShiftReportingProps {
  /** OFD date (YYYY-MM-DD) shared from the dashboard controls. */
  ofdDate?: string;
}

const EndOfShiftReporting: React.FC<EndOfShiftReportingProps> = ({ ofdDate }) => {
  const [view, setView] = useState<ShiftView>('combined');
  const [data, setData] = useState<EosReportingData | null>(null);
  const [loading, setLoading] = useState(true);
  const stationCode = useAppSelector(selectStationCode);
  const dispatch = useAppDispatch();

  // EOS spans both shifts, so the picker lists plans from Day and Night.
  // `selectedPlanId` stays null until the operator picks one, leaving the default
  // report on the baseline the backend resolves.
  const { groups: planGroups, loading: plansLoading } = useReportPlans({
    ofdDate,
    shiftTypes: [ShiftTypes.DAY_SHIFT, ShiftTypes.NIGHT_SHIFT],
    skip: !stationCode,
  });
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // A plan chosen for one date is meaningless for another.
  useEffect(() => {
    setSelectedPlanId(null);
  }, [ofdDate]);

  useEffect(() => {
    let cancelled = false;

    if (!stationCode || !ofdDate) {
      setData({ day: null, night: null });
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchEosReportingData(stationCode, ofdDate, selectedPlanId ?? undefined)
      .then(result => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch(showError({ message: 'Failed to fetch End of Shift data', title: 'API Error' }));
          setData({ day: null, night: null });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ofdDate, stationCode, selectedPlanId, dispatch]);

  // Rendered in every branch, including the loading and empty states: if a picked
  // plan has no EOS data the operator still needs the control to pick another.
  const planPicker = (
    <ReportPlanSelector
      groups={planGroups}
      loading={plansLoading}
      value={selectedPlanId ?? resolveDefaultPlanId(planGroups, data?.resolvedBaselinePlanId)}
      onChange={setSelectedPlanId}
      label="Reporting on plan"
      testId="eos-plan-select"
    />
  );

  if (loading) {
    return (
      <Column spacing="400" data-testid="end-of-shift-reporting">
        {planPicker}
        <Column spacing="200" alignmentHorizontal="center">
          <Loader data-testid="eos-loader" />
        </Column>
      </Column>
    );
  }

  const day = data?.day ?? null;
  const night = data?.night ?? null;

  if (!day && !night) {
    return (
      <Column spacing="400" data-testid="end-of-shift-reporting">
        {planPicker}
        <Column spacing="200" alignmentHorizontal="center">
          <Text type="b300" color="secondary" data-testid="eos-empty-state">
            End of Shift Reporting data is not available yet.
          </Text>
        </Column>
      </Column>
    );
  }

  const showDay = day && (view === 'day' || view === 'combined');
  const showNight = night && (view === 'night' || view === 'combined');
  // Selected single-shift view is the one with no data (the other loaded).
  const noDataForView = !showDay && !showNight;

  // Scope the blue palette to this view (the app theme is purple).
  return (
    <Theme palette="blue">
      <Column spacing="400" data-testid="end-of-shift-reporting">
        {planPicker}
        <Row spacing="400" alignmentVertical="center">
          <Heading level={2} type="h400">
            Consolidated EOS Reporting
          </Heading>
          <Row spacing="200" widths="fit" alignmentVertical="center">
            {VIEW_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                type={opt.value === view ? 'primary' : 'secondary'}
                size="small"
                onClick={() => setView(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Row>
        </Row>

        {/* Header rule — mirrors the mock's navy border under the heading. */}
        <Box backgroundColor={colorBlue600} height={3} />

        {showDay && <ShiftBlock shift={day} />}
        {showNight && <ShiftBlock shift={night} />}
        {noDataForView && (
          <Text type="b300" color="secondary" data-testid="eos-view-empty">
            No End of Shift data available for the selected shift.
          </Text>
        )}
      </Column>
    </Theme>
  );
};

export default EndOfShiftReporting;
